<template>
  <div class="filters-list">
    <FilterItem
      v-for="filter in filterList"
      :key="filter.filterKey"
      :is-toggle="filter.isToggle"
      :classes="filter.customClasses"
      :selected-classes="filter.customSelectedClasses"
      v-bind="filter"
      @search-updated="filter.emits['search-updated']"
      @filters-updated="filter.emits['filters-updated']"
    >
      <template v-if="filter.icon" #prefix>
        <component :is="filter.icon" class="mr-2" />
      </template>
      <template v-if="filter.arrowIcon" #suffix>
        <IconChevronDown class="ml-2 anchor" />
      </template>
      <template
        v-if="filter.contentComponent"
        #content="{ modelValue, updated }"
      >
        <component
          :is="filter.contentComponent"
          :model-value="modelValue"
          v-bind="modelValue"
          @updated="updated"
        />
      </template>
    </FilterItem>
  </div>
</template>

<script lang="ts" setup>
import { useDebounceFn } from '@vueuse/core'
import { diceCoefficient } from 'dice-coefficient'
import IconGeo from '~/public/icons/icon-geo.svg?component'
import IconTags from '~/public/icons/icon-tag.svg?component'
import IconFilterCircle from '~/components/icons/IconFilterCircle'
import IconChevronDown from '~/public/icons/chevron-down.svg?component'
import IconBolt from '~/public/icons/lightning-charge-fill.svg?component'
import FilterItemOthers from '~/components/filter/FilterItemOthers.vue'

import { useAlgoliaLocalities } from '~/composables/algolia/useAlgoliaLocalities'
import type { SearchFilterOptions } from '~/composables/useSearchFilters'

const emit = defineEmits(['filtersUpdated', 'searchUpdated'])
const { t } = useI18n()
const { searchLocalities, setCitySlug } = await useAlgoliaLocalities('', false)

const props = defineProps<{
  facets?: object
  filters?: SearchFilterOptions
}>()
const categoriesFilterQuery = ref<string | undefined>(undefined)

const categories = computed(() => {
  return (
    Object.keys(props.facets?.['categories.name'] || {})
      .map((d) => ({
        slug: d,
        name: d,
        count: props?.facets?.['categories.name'][d],
        similarityScore: categoriesFilterQuery.value
          ? diceCoefficient(categoriesFilterQuery.value, d)
          : 1,
      }))
      .sort((a, b) => {
        if (categoriesFilterQuery.value) {
          return a.similarityScore > b.similarityScore ? -1 : 1
        } else {
          return a.name > b.name ? 1 : a.name < b.name ? -1 : 0
        }
      })
      // only show up to 10 categories if there is a query
      .slice(0, categoriesFilterQuery.value ? 10 : undefined)
  )
})

const selectedDates = computed(() =>
  // getting unique dates
  props.filters?.dates?.filter((x, i, arr) => arr.indexOf(x) === i)
)

const otherFilters = computed(() => ({
  dates: selectedDates.value,
  typeOfClass: props.filters?.typeOfClass,
  hasAvailableDates: props.filters?.hasAvailableDates,
  languages: props.filters?.languages?.slice(0),
  priceMin: props.filters?.priceMin,
  priceMax: props.filters?.priceMax,
  directEvents: props.filters?.directEvents,
  publicEvents: props.filters?.publicEvents,
  peopleAmount: props.filters?.peopleAmount,
}))

const cities = computed(() => props?.facets?.city)

const onLocalityUpdated = (updatedVal) => {
  setCitySlug(updatedVal.city)
  emit('filtersUpdated', updatedVal, 'city')
}

const debouncedSearchLocalities = useDebounceFn((city: string) => {
  searchLocalities(city)
})

const debouncedSearchCategories = useDebounceFn((query: string) => {
  categoriesFilterQuery.value = query
}, 600)

const filterList = computed(() => [
  {
    icon: IconGeo,
    arrowIcon: true,
    label: t('search.filters.city.label'),
    options: cities.value,
    dialogTitle: t('search.selectLocalityComponent.title'),
    filterKey: 'city',
    showSearch: true,
    selectedOptions: props.filters?.city ? [props.filters?.city] : [],
    getLabel: () =>
      cities.value?.find((c) => c.slug === props.filters?.city)?.name,
    placeholder: t('search.selectLocalityComponent.searchPlaceholder'),
    isButtonHide: true,
    emits: {
      'search-updated': ($event) => {
        debouncedSearchLocalities($event)
      },
      'filters-updated': ($event) => {
        onLocalityUpdated($event)
      },
    },
  },
  {
    icon: IconTags,
    arrowIcon: true,
    label: t('search.filters.category.chipLabel'),
    options: categories.value,
    dialogTitle: t('search.filters.category.label'),
    showSearch: true,
    selectedOptions: props.filters?.categories || [],
    multiSelect: true,
    placeholder: t('search.selectCategoryComponent.searchPlaceholder'),
    filterKey: 'categories',
    emits: {
      'search-updated': ($event) => {
        debouncedSearchCategories($event)
      },
      'filters-updated': ($event) => {
        emit('filtersUpdated', $event, 'categories')
      },
    },
  },
  {
    icon: IconFilterCircle,
    arrowIcon: true,
    // dialogTitle: t('search.selectFiltersComponent.label'),
    label: t('search.selectFiltersComponent.label'),
    filterKey: 'other',
    selectedOptions: otherFilters?.value,
    emits: {
      'filters-updated': ($event) => {
        emit('filtersUpdated', $event)
      },
      'calendar-updated': ($event) => {
        emit('filtersUpdated', $event, 'dates')
      },
    },
    contentComponent: FilterItemOthers,
  },
  {
    icon: IconBolt,
    label: t('search.selectFiltersPromotions.label'),
    filterKey: 'hasPromotion',
    selectedOptions: [!!props.filters.hasPromotion],
    emits: {
      'filters-updated': ($event) => {
        emit('filtersUpdated', $event)
      },
    },
    contentComponent: FilterItemOthers,
    isToggle: true,
    customClasses: '[&>svg]:fill-[#FF6A39] hover:bg-[#FFFCF3]',
    customSelectedClasses:
      '[&>svg]:fill-[#FF6A39] bg-[#FFFCF3] !border-[#FFE1D4] text-[#C7491F]',
  },
])

// to detect if dropdown active in filters
const activeFilterItemId = ref(null)
provide('activeFilterItemId', activeFilterItemId)
</script>

<style lang="postcss" scoped>
.filters-list {
  @apply flex flex-wrap gap-1 justify-start;
}
</style>
